// Utils
import { getListChildNewLocation, isLocationCanvas } from '../../../common/elements/utils/elementLocationUtils';
import { isCard } from '../../../common/elements/utils/elementTypeUtils';
import { prop } from '../../../common/utils/immutableHelper';
import {
    getElementId,
    getElementType,
    getLocationParentId,
    getPositionIndex,
    getWidth,
    getXPosition,
    getYPosition,
} from '../../../common/elements/utils/elementPropertyUtils';

// Selectors
import { getCurrentBoard, getCurrentBoardIdFromState } from '../../reducers/currentBoardId/currentBoardIdSelector';
import { getMeasurementsMapThunk } from '../../components/measurementsStore/elementMeasurements/elementMeasurementsSelector';
import getGridSize from '../../utils/grid/gridSizeSelector';

// Actions
import { closeInbox, openInbox } from '../../inbox/inboxActions';
import { createAndEditElement } from '../../element/actions/elementActions';
import { createTaskList } from '../../element/taskList/taskListActions';
import { createTable } from '../../element/table/tableActions';

import { getSelectedElements } from '../../element/selection/selectedElementsSelector';
import { getCurrentlyEditingId } from '../../element/selection/currentlyEditingSelector';

// Constants
import { ELEMENT_CREATION_SOURCES, ELEMENT_DEFAULT_WIDTH } from '../../../common/elements/elementConstants';
import { BoardSections, InboxState } from '../../../common/boards/boardConstants';
import { ElementType } from '../../../common/elements/elementTypes';

const getLocationOnCanvasUnder = (element, measurementsDispatch) => (dispatch, getState) => {
    const elementId = getElementId(element);
    const state = getState();

    const gridSize = getGridSize(state);

    const measurementsMap = measurementsDispatch(getMeasurementsMapThunk());
    const elementMeasurements = prop(elementId, measurementsMap);

    return {
        parentId: getLocationParentId(element),
        section: BoardSections.CANVAS,
        position: {
            x: getXPosition(element),
            y: getYPosition(element) + (prop('height', elementMeasurements) / gridSize + 1 || 8),
        },
    };
};

const createNewElementUnder =
    ({ element, measurementsDispatch, elementType = ElementType.CARD_TYPE }) =>
    (dispatch, getState) => {
        const state = getState();

        const currentBoardId = getCurrentBoardIdFromState(state);

        const parentId = getLocationParentId(element);
        const elementIndex = getPositionIndex(element);

        const location = isLocationCanvas(element)
            ? dispatch(getLocationOnCanvasUnder(element, measurementsDispatch))
            : getListChildNewLocation({ listId: parentId, index: elementIndex + 1 });

        const content = {};

        if (isCard(elementType) && getWidth(element)) {
            content.width = getWidth(element);
        }

        dispatch(
            createAndEditElement({
                creationSource: ELEMENT_CREATION_SOURCES.CMD_ENTER,
                elementType,
                location,
                currentBoardId,
                content,
                select: true,
            }),
        );
    };

const createNewElementNextTo =
    ({ element, elementType = ElementType.CARD_TYPE, distance = ELEMENT_DEFAULT_WIDTH + 2 }) =>
    (dispatch, getState) => {
        const state = getState();

        const currentBoardId = getCurrentBoardIdFromState(state);

        const parentId = getLocationParentId(element);
        const elementIndex = getPositionIndex(element);

        const location = isLocationCanvas(element)
            ? {
                  parentId,
                  section: BoardSections.CANVAS,
                  position: {
                      // 17 seems to position the boards at a nice distance apart
                      x: getXPosition(element) + distance,
                      y: getYPosition(element),
                  },
              }
            : getListChildNewLocation({ listId: parentId, index: elementIndex + 1 });

        dispatch(
            createAndEditElement({
                creationSource: ELEMENT_CREATION_SOURCES.CMD_ENTER,
                elementType,
                location,
                currentBoardId,
                content: {},
                select: true,
            }),
        );
    };

const makeCreateNewElementFn =
    (creationActionFn) =>
    ({ element, measurementsDispatch }) =>
    (dispatch, getState) => {
        const state = getState();

        const currentBoardId = getCurrentBoardIdFromState(state);

        const parentId = getLocationParentId(element);
        const elementIndex = getPositionIndex(element);

        const location = isLocationCanvas(element)
            ? dispatch(getLocationOnCanvasUnder(element, measurementsDispatch))
            : getListChildNewLocation({ listId: parentId, index: elementIndex + 1 });

        return dispatch(
            creationActionFn({
                creationSource: ELEMENT_CREATION_SOURCES.CMD_ENTER,
                currentBoardId,
                location,
            }),
        );
    };

const createNewTaskList = makeCreateNewElementFn(createTaskList);
export const createNewTable = makeCreateNewElementFn(createTable);

const handleCreateElementOfType = (element, measurementsDispatch) => (dispatch, getState) => {
    const elementType = getElementType(element);

    const state = getState();

    const currentlyEditingId = getCurrentlyEditingId(state);

    switch (elementType) {
        case ElementType.BOARD_TYPE:
            // 17 to the right seems to create good spacing in most circumstances
            return dispatch(createNewElementNextTo({ element, elementType: ElementType.BOARD_TYPE, distance: 17 }));
        case ElementType.COLUMN_TYPE:
            return dispatch(createNewElementNextTo({ element, elementType: ElementType.COLUMN_TYPE }));
        case ElementType.DOCUMENT_TYPE:
            return dispatch(createNewElementNextTo({ element, elementType: ElementType.DOCUMENT_TYPE, distance: 17 }));
        case ElementType.COMMENT_THREAD_TYPE:
            return dispatch(
                createNewElementUnder({ element, measurementsDispatch, elementType: ElementType.COMMENT_THREAD_TYPE }),
            );
        case ElementType.CARD_TYPE:
            // Don't handle cards if they're editing as this is already handled in the MilanoteEditor
            return currentlyEditingId
                ? null
                : dispatch(
                      createNewElementUnder({ element, measurementsDispatch, elementType: ElementType.CARD_TYPE }),
                  );
        // Don't do anything with images or lines
        case ElementType.TABLE_TYPE:
        case ElementType.LINE_TYPE:
        case ElementType.IMAGE_TYPE:
            return;
        case ElementType.TASK_LIST_TYPE:
            return dispatch(createNewTaskList({ element, measurementsDispatch }));
        default:
            return dispatch(
                createNewElementUnder({ element, measurementsDispatch, elementType: ElementType.CARD_TYPE }),
            );
    }
};

export const createNewElementOfSelectedType =
    (event, { measurementsDispatch }) =>
    (dispatch, getState) => {
        const state = getState();

        const selectedElements = getSelectedElements(state);
        if (selectedElements.size !== 1) return;

        const selectedElement = selectedElements.first();

        return dispatch(handleCreateElementOfType(selectedElement, measurementsDispatch));
    };

/**
 * Opens or closes unsorted notes.
 */
export const toggleUnsorted = () => (dispatch, getState) => {
    const state = getState();
    const currentBoardId = getCurrentBoardIdFromState(state);
    const currentBoard = getCurrentBoard(state);

    if (!currentBoard) return;
    if (currentBoard.getIn(['content', 'inbox']) === InboxState.OPEN) {
        return dispatch(closeInbox({ id: currentBoardId }));
    }

    dispatch(openInbox({ id: currentBoardId }));
};
